import { ChainID } from '@ankr.com/chains-list';
import { UserEndpointTokenMode } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { IFetchPrivateStatsParams } from 'modules/stats/actions/fetchPrivateStats';
import { RootState } from 'store';
import { getPrivateStatsProjectName } from 'modules/stats/utils/getPrivateStatsProjectName';
import { getUniqueArray } from 'modules/common/utils/getUniqueArray';
import { hasAllBlockchainsInWhitelist } from 'modules/projects/utils/hasAllBlockchainsInWhitelist';
import { selectPrivateAPIKeysCounts } from 'modules/stats/store/private/selectors';
import {
  selectBlockchainBySubchainId,
  selectChainIdsByPaths,
} from 'modules/chains/store/selectors';
import { selectUIProfileConfig } from 'modules/common/actions/getUIProfileConfig';
import { selectJWTs } from 'domains/jwtToken/action/fetchJWTs';

import {
  IFetchProjectsWhitelistsBlockchainsParams,
  selectProjectsWhitelistsBlockchains,
} from '../actions/fetchProjectsWhitelistsBlockchains';
import { selectProjectWhitelist } from '../actions/fetchProjectWhitelist';
import { IFetchProjectsWhitelistsParams } from '../actions/fetchProjectsWhitelists';

export const selectDraftUserEndpointToken = createSelector(
  selectUIProfileConfig,
  config => {
    if (!config || !config.draftProject) {
      return undefined;
    }

    return config.draftProject.userEndpointToken;
  },
);

export const selectDraftProjectStep = createSelector(
  selectUIProfileConfig,
  config => {
    if (!config || !config.draftProject) {
      return undefined;
    }

    return config.draftProject.step;
  },
);

export const selectDraftProject = createSelector(
  selectJWTs,
  selectDraftUserEndpointToken,
  (projects, draftEndpointToken) =>
    projects.find(project => project.userEndpointToken === draftEndpointToken),
);

export const selectProjectWhitelistByType = createSelector(
  selectProjectWhitelist,
  (_state: RootState, { type }: { type: UserEndpointTokenMode }) => type,
  (whitelist, type) =>
    whitelist?.lists?.filter(item => item.type === type) ?? [],
);

export const selectProjectRequestsCount = createSelector(
  selectPrivateAPIKeysCounts,
  (_state: RootState, _params: IFetchPrivateStatsParams, token: string) =>
    token,
  (counts, token) => {
    const projectName = getPrivateStatsProjectName(token);

    return Number(counts.find(({ name }) => name === projectName)?.count ?? 0);
  },
);

export const selectHasProjectsWithLastChain = createSelector(
  selectProjectsWhitelistsBlockchains,
  (
    state: RootState,
    _params: IFetchProjectsWhitelistsBlockchainsParams,
    chainId: ChainID,
  ) => ({ state, chainId }),
  (projects, { chainId: relatedChainId, state }) =>
    projects.some(project => {
      if (project.blockchains) {
        if (hasAllBlockchainsInWhitelist(project.blockchains)) {
          return false;
        }

        const chainIds = selectChainIdsByPaths(state, project.blockchains);

        const chains = chainIds
          .map(chainId => selectBlockchainBySubchainId(state, chainId))
          .filter(Boolean);

        const mainChainsIds = getUniqueArray(chains.map(chain => chain?.id));

        const isChainMainIdRelatedToCurrentSelector = mainChainsIds.some(
          chainId => chainId === relatedChainId,
        );

        return (
          mainChainsIds.length === 1 && isChainMainIdRelatedToCurrentSelector
        );
      }
    }),
);

export const selectAllProjectsWhitelists = createSelector(
  (state: RootState, params: IFetchProjectsWhitelistsParams) => ({
    state,
    params,
  }),
  ({ params: { group, projects }, state }) =>
    projects.map(project => ({
      ...project,
      whitelists: selectProjectWhitelist(state, {
        userEndpointToken: project.userEndpointToken,
        group: group,
      }),
    })),
);
